import { useMutation } from 'react-query';
import { Box, Divider, HStack, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Button, ControlInput, Text } from '@pimm/base';
import { ChangePassword } from '@pimm/services/lib/sms-tenants/services';
import { ChangePasswordRequest, PasswordResetDto } from '@pimm/services/lib/sms-tenants';
import { useFormPasswordUpdate } from '../hooks/form-password-update.hook';
import { ListItem } from './list-item';

type FormPasswordUpdateProps = {
  userId: string;
  onCancel?: () => void;
  onEndChange: (payload?: PasswordResetDto) => void;
};

export const FormPasswordUpdate = ({ userId, ...props }: FormPasswordUpdateProps) => {
  const form = useFormPasswordUpdate();
  const { t } = useTranslation();
  const changePassword = useMutation({
    mutationFn: ChangePassword,
    onSuccess: data => props.onEndChange(data),
    onError: (error: { code: number; message: string }) => {
      // Reset input fields but keep the error message
      form.resetField('newPassword');
      form.resetField('confirmPassword');

      if (error.code === 400) {
        form.setError('currentPassword', {
          type: 'custom',
          message: 'account_settings:invalid_password',
        });
      } else {
        form.setError('root', {
          type: 'custom',
          message: 'account_settings:error_try_again',
        });
      }
    },
  });

  const handleUpdatePassword = () => {
    const { currentPassword, newPassword } = form.getValues();
    const payload: ChangePasswordRequest = {
      userId: userId,
      newPassword: btoa(newPassword),
      oldPassword: btoa(currentPassword),
    };
    changePassword.mutate(payload);
  };

  return (
    <VStack height="full" w="full">
      <HStack alignItems="center" px={4} h="52px" borderBottomWidth={1}>
        <Text size="xl" fontWeight={700} color="gray.900">
          {t('account_settings:password_update')}
        </Text>
      </HStack>

      <VStack space={4} p={4}>
        <Box pb={2}>
          <Text fontWeight={700} size="md" color="gray.700" lineHeight="xs">
            {t('account_settings:password_change_prompt')}
          </Text>
        </Box>

        {/* Current Password */}
        <ListItem _title={{ w: '1/3', h: 9, justifyContent: 'center' }} text={t('account_settings:current_password')}>
          <ControlInput control={form.control} name="currentPassword" type="password" renderErrorText={_err => t(_err ?? '')} />
        </ListItem>

        <Divider bg="gray.200" />

        {/* New Password */}
        <ListItem _title={{ w: '1/3', h: 9, justifyContent: 'center' }} text={t('account_settings:new_password')}>
          <ControlInput control={form.control} name="newPassword" type="password" renderErrorText={_err => t(_err ?? '')} />
          <Text fontWeight={500} size="md" color="gray.600" lineHeight="xs" mt={2}>
            {t('account_settings:password_requirements')}
          </Text>
        </ListItem>

        <Divider bg="gray.200" />

        {/* Confirm New Password */}
        <ListItem _title={{ w: '1/3', h: 9, justifyContent: 'center' }} text={t('account_settings:confirm_new_password')}>
          <ControlInput control={form.control} name="confirmPassword" type="password" renderErrorText={_err => t(_err ?? '')} />
        </ListItem>
      </VStack>

      <HStack alignItems="center" py={3} px={4} borderTopWidth={1}>
        <Box flex={1} pr={1}>
          {!!form.formState.errors.root && (
            <Text size="md" fontWeight={500} color="error.500" lineHeight="xs" numberOfLines={2}>
              {form.formState.errors.root.message ? t(form.formState.errors.root.message) : ''}
            </Text>
          )}
        </Box>

        <HStack space={3} justifyContent="flex-end">
          <Button variant="unstyled" minWidth={120} disabled={changePassword.isLoading} onPress={props.onCancel}>
            {t('account_settings:cancel')}
          </Button>

          <Button minW={120} isLoading={changePassword.isLoading} onPress={form.handleSubmit(handleUpdatePassword)}>
            {t('account_settings:update_password')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};
